import { useCallback, useEffect, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiPrinter,
  FiRotateCcw,
  FiTruck,
  FiX,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value, withTime = false) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime
      ? { hour: "2-digit", minute: "2-digit", hour12: true }
      : {}),
  }).format(new Date(value));
};

const getStatusStyle = (status) => {
  const styles = {
    DRAFT: "bg-amber-100 text-amber-700",
    DISPATCHED: "bg-blue-100 text-blue-700",
    CLOSED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return styles[status] || "bg-gray-100 text-gray-700";
};

export default function SupplierReturnDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [supplierReturn, setSupplierReturn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDispatching, setIsDispatching] = useState(false);
  const [debitNote, setDebitNote] = useState(null);
  const [isDebitNoteLoading, setIsDebitNoteLoading] = useState(false);
  const [showDebitNote, setShowDebitNote] = useState(false);
  const [error, setError] = useState("");

  const loadSupplierReturn = useCallback(async () => {
    if (!id || !accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest(`/purchases/returns/${id}`, {
        accessToken,
      });

      setSupplierReturn(response);
    } catch (requestError) {
      setError(
        requestError.message ||
          "Could not load the supplier return. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadSupplierReturn();
  }, [loadSupplierReturn]);

  const handleDispatch = async () => {
    if (!window.confirm("Dispatch this supplier return? Inventory will be reduced.")) {
      return;
    }

    try {
      setIsDispatching(true);
      setError("");

      await apiRequest(`/purchases/returns/${id}/dispatch`, {
        method: "POST",
        accessToken,
      });

      await loadSupplierReturn();
    } catch (requestError) {
      setError(
        requestError.message ||
          "Could not dispatch the supplier return. Please try again.",
      );
    } finally {
      setIsDispatching(false);
    }
  };

  const loadDebitNote = useCallback(async () => {
    if (!id || !accessToken) return null;

    setIsDebitNoteLoading(true);
    setError("");
    try {
      const response = await apiRequest(`/purchases/returns/${id}/debit-note`, {
        accessToken,
      });
      setDebitNote(response);
      return response;
    } catch (requestError) {
      setError(requestError.message || "Could not prepare the debit note.");
      return null;
    } finally {
      setIsDebitNoteLoading(false);
    }
  }, [accessToken, id]);

  const handleOpenDebitNote = async () => {
    const document = debitNote || (await loadDebitNote());
    if (document) setShowDebitNote(true);
  };

  const handlePrint = async () => {
    const document = debitNote || (await loadDebitNote());
    if (!document) return;
    setShowDebitNote(true);
    window.setTimeout(() => window.print(), 100);
  };

  const handleExport = async () => {
    try {
      setError("");
      const response = await apiRequest(
        `/purchases/returns/${id}/debit-note/export`,
        { accessToken, rawResponse: true },
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${supplierReturn?.returnNumber || "supplier-return"}-debit-note.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message || "Could not export the debit note CSV.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">
        Loading supplier return...
      </div>
    );
  }

  if (error && !supplierReturn) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
        <div className="flex gap-3 text-red-700">
          <FiAlertTriangle className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Could not load supplier return</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>

        <button
          onClick={() => navigate("/admindashboard/purchases/returns")}
          className="mt-5 h-10 px-4 rounded-xl bg-primary-700 text-white"
        >
          Back to Returns
        </button>
      </div>
    );
  }

  const items = supplierReturn?.items || [];
  const totalQuantity = items.reduce(
    (sum, item) => sum + Number(item.quantityReturned || 0),
    0,
  );
  // Use the supplier's original product rate. The stored lot rate can include
  // allocated freight and is retained only for inventory valuation.
  const totalValue = Number(
    supplierReturn?.commercialTotals?.productValue ??
      items.reduce(
        (sum, item) =>
          sum + Number(item.commercialLineTotal ?? item.lineTotal ?? 0),
        0,
      ),
  );

  const goodsReceipt = supplierReturn?.goodsReceipt;
  const purchaseOrder = goodsReceipt?.purchaseOrder;
  const isDraft = supplierReturn?.status === "DRAFT";

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <p className="text-sm text-gray-500">
            Purchasing / Supplier Returns
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-1">
            <h1 className="text-3xl font-bold text-gray-900">
              {supplierReturn?.returnNumber}
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusStyle(
                supplierReturn?.status,
              )}`}
            >
              {supplierReturn?.status}
            </span>
          </div>

          <p className="text-gray-500 mt-2">
            Created on {formatDate(supplierReturn?.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {isDraft && (
            <button
              onClick={handleDispatch}
              disabled={isDispatching}
              className="h-11 px-4 rounded-xl bg-primary-700 hover:bg-primary-800 disabled:opacity-60 text-white flex items-center gap-2 font-medium"
            >
              <FiTruck />
              {isDispatching ? "Dispatching..." : "Dispatch Return"}
            </button>
          )}

          <ActionButton
            icon={<FiFileText />}
            label={isDebitNoteLoading ? "Preparing..." : "Debit Note"}
            onClick={handleOpenDebitNote}
          />

          <ActionButton icon={<FiPrinter />} label="Print Debit Note" onClick={handlePrint} />

          <ActionButton
            icon={<FiDownload />}
            label="Export CSV"
            onClick={handleExport}
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-100 text-red-700 rounded-2xl px-5 py-4 text-sm">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-5 mb-6">
        <OverviewCard
          title="Supplier"
          value={supplierReturn?.supplier?.displayName || "—"}
        />

        <OverviewCard
          title="GRN"
          value={goodsReceipt?.grnNumber || "—"}
        />

        <OverviewCard
          title="Purchase Order"
          value={purchaseOrder?.purchaseOrderNumber || "—"}
        />

        <OverviewCard
          title="Product Return Value"
          value={formatCurrency(totalValue)}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-gray-100 rounded-3xl p-6">
          <h2 className="font-semibold text-lg mb-5">Return Information</h2>

          <div className="space-y-4">
            <Info label="Return Number" value={supplierReturn?.returnNumber} />
            <Info
              label="Purchase Order"
              value={purchaseOrder?.purchaseOrderNumber}
            />
            <Info label="GRN Number" value={goodsReceipt?.grnNumber} />
            <Info label="Status" value={supplierReturn?.status} />
            <Info label="Notes" value={supplierReturn?.notes || "—"} />
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-3xl p-6">
          <h2 className="font-semibold text-lg mb-5">Financial Information</h2>

          <div className="space-y-4">
            <Info
              label="Product Return Value (excl. freight)"
              value={formatCurrency(totalValue)}
            />
            <Info label="Units Returned" value={totalQuantity} />
            <Info
              label="Created On"
              value={formatDate(supplierReturn?.createdAt, true)}
            />
            <Info
              label="Dispatched On"
              value={formatDate(supplierReturn?.dispatchedAt, true)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-100">
          <h2 className="font-semibold text-lg">Returned Products</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50 text-sm text-gray-600">
                <th className="px-6 py-4 text-left font-medium">Product</th>
                <th className="px-6 py-4 text-left font-medium">Source Lot</th>
                <th className="px-6 py-4 text-center font-medium">Returned</th>
                <th className="px-6 py-4 text-right font-medium">Unit Cost</th>
                <th className="px-6 py-4 text-left font-medium">Reason</th>
                <th className="px-6 py-4 text-right font-medium">Amount</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-gray-100">
                  <td className="px-6 py-4">
                    <h4 className="font-medium text-gray-900">
                      {item.variant?.product?.name || "Unknown Product"}
                    </h4>

                    <p className="text-xs text-gray-500 mt-1">
                      SKU: {item.variant?.sku || "—"}
                    </p>
                  </td>

                  <td className="px-6 py-4">
                    {item.supplierReceiptItem?.inventoryLot ? (
                      <>
                        <p className="font-mono text-sm font-semibold text-primary-800">
                          {item.supplierReceiptItem.inventoryLot.lotNumber}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Available now: {Math.max(
                            0,
                            Number(
                              item.supplierReceiptItem.inventoryLot
                                .remainingQuantity || 0,
                            ) -
                              Number(
                                item.supplierReceiptItem.inventoryLot
                                  .reservedQuantity || 0,
                              ),
                          )}
                        </p>
                      </>
                    ) : (
                      <span className="text-xs font-medium text-red-600">
                        Legacy / unavailable lot
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-center text-red-600 font-semibold">
                    {Number(item.quantityReturned || 0)}
                  </td>

                  <td className="px-6 py-4 text-right">
                    {formatCurrency(item.commercialUnitCost ?? item.unitCost)}
                  </td>

                  <td className="px-6 py-4">
                    <span className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-medium">
                      {item.reason || "Not specified"}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-right font-semibold">
                    {formatCurrency(item.lineTotal)}
                  </td>
                </tr>
              ))}

              {!items.length && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No returned items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <SummaryCard
          title="Units Returned"
          value={totalQuantity}
          icon={<FiRotateCcw />}
          color="red"
        />

        <SummaryCard
          title="Inventory Impact"
          value={supplierReturn?.status === "DISPATCHED" ? `-${totalQuantity}` : "Pending"}
          icon={<FiTruck />}
          color="orange"
        />

        <SummaryCard
          title="Product Return Value"
          value={formatCurrency(totalValue)}
          icon={<FiFileText />}
          color="green"
        />
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl p-6 mt-6">
        <h2 className="font-semibold text-lg mb-6">Activity Timeline</h2>

        <div className="space-y-5">
          <TimelineItem
            title="Supplier Return Created"
            time={formatDate(supplierReturn?.createdAt, true)}
          />

          {supplierReturn?.dispatchedAt && (
            <TimelineItem
              title="Return Dispatched and Inventory Reduced"
              time={formatDate(supplierReturn.dispatchedAt, true)}
            />
          )}

          {supplierReturn?.status === "DRAFT" && (
            <TimelineItem
              title="Awaiting Dispatch"
              time="Inventory will reduce only after dispatch."
              warning
            />
          )}
        </div>
      </div>

      {showDebitNote && debitNote && (
        <DebitNoteModal
          debitNote={debitNote}
          onClose={() => setShowDebitNote(false)}
          onPrint={() => window.print()}
        />
      )}
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="h-11 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-2"
    >
      {icon}
      {label}
    </button>
  );
}

function OverviewCard({ title, value }) {
  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className="text-xl font-bold text-gray-900 mt-2 break-words">{value}</h3>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-medium text-gray-900">{value || "—"}</p>
    </div>
  );
}

function SummaryCard({ title, value, icon, color }) {
  const colors = {
    red: "bg-red-50 text-red-600",
    orange: "bg-orange-50 text-orange-600",
    green: "bg-green-50 text-green-600",
  };

  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900 mt-2">{value}</h3>
        </div>

        <div className={`h-14 w-14 shrink-0 rounded-2xl flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function TimelineItem({ title, time, warning = false }) {
  return (
    <div className="flex gap-4">
      <div
        className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${
          warning ? "bg-amber-50 text-amber-600" : "bg-primary-50 text-primary-700"
        }`}
      >
        {warning ? <FiAlertTriangle /> : <FiCheckCircle />}
      </div>

      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{time}</p>
      </div>
    </div>
  );
}

function DebitNoteModal({ debitNote, onClose, onPrint }) {
  const supplier = debitNote.supplier || {};
  const store = debitNote.issuingStore || {};
  const company = debitNote.company || {};
  const purchase = debitNote.originalPurchase || {};
  const totals = debitNote.totals || {};
  const address = (party) =>
    [
      party.addressLine1,
      party.addressLine2,
      [party.city, party.state, party.postalCode].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(", ");
  const documentDate = formatDate(debitNote.debitNoteDate);
  const companyName = company.legalName || company.displayName || "CHARMS";

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-950/60 p-3 sm:p-6 print:bg-white print:p-0">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #debit-note-print, #debit-note-print * { visibility: visible !important; }
          #debit-note-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; }
          .debit-note-actions { display: none !important; }
          @page { size: A4 portrait; margin: 8mm; }
        }
      `}</style>

      <div className="debit-note-actions mx-auto mb-3 flex max-w-[900px] justify-end gap-3">
        <button type="button" onClick={onPrint} className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-gray-800 shadow"><FiPrinter /> Print / Save PDF</button>
        <button type="button" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-gray-700 shadow" aria-label="Close debit note"><FiX size={20} /></button>
      </div>

      <article id="debit-note-print" className="mx-auto max-w-[900px] bg-white p-3 font-serif text-[10px] text-black shadow-2xl sm:p-6">
        <header className="border border-black text-center leading-tight">
          <p className="border-b border-black py-1 text-lg font-bold">PURCHASE RETURN</p>
          <p className="border-b border-black py-1 text-xl font-black">{companyName}</p>
          <p className="border-b border-black py-1 text-sm font-bold">H.O.: {address(company) || companyName}</p>
          <p className="border-b border-black py-1 text-sm font-bold">B.O.: {address(store) || store.name || "—"}</p>
          <p className="py-1 text-sm font-bold">GST No.: {company.gstin || "—"}{company.pan ? ` · PAN: ${company.pan}` : ""}{company.phone ? ` · ${company.phone}` : ""}</p>
        </header>

        <section className="mt-4 grid grid-cols-[1.1fr_1.4fr] border border-black">
          <div className="min-h-[175px] border-r border-black">
            <p className="border-b border-black px-2 py-1 text-sm font-bold">To</p>
            <div className="space-y-1 p-2 text-sm font-bold leading-snug">
              <p>{supplier.legalName || supplier.displayName || "—"}</p>
              <p>{address(supplier) || "—"}</p>
              <p>Phone No. {supplier.phone || "—"}</p>
              <p>GST NO {supplier.gstin || "—"}</p>
            </div>
          </div>
          <div className="grid grid-cols-[145px_1fr] content-start text-sm">
            <PaperField label="Debit Note No" value={debitNote.debitNoteNumber} />
            <PaperField label="Date" value={documentDate} />
            <PaperField label="Payment Mode" value="Credit" />
            <PaperField label="Remarks" value={debitNote.notes || debitNote.reason || "—"} />
            <PaperField label="E-WAY NO." value="—" />
            <PaperField label="ACK NO." value="—" />
            <PaperField label="ACK DT." value="—" />
            <PaperField label="IRN NO." value="—" />
          </div>
        </section>

        <section className="mt-4 border border-black">
          <table className="w-full border-collapse text-[10px]">
            <thead className="font-sans text-[10px] font-bold">
              <tr>
                {[["S.No.", "w-[5%]"], ["PUR BILL NO", "w-[13%]"], ["PUR DATE", "w-[10%]"], ["ITEM NAME", "w-[28%]"], ["Pack", "w-[10%]"], ["QTY", "w-[7%]"], ["HSN", "w-[11%]"], ["RATE", "w-[8%]"], ["AMT", "w-[8%]"]].map(([heading, width]) => <th key={heading} className={`border-b border-r border-black px-1 py-2 text-center last:border-r-0 ${width}`}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {debitNote.lines.map((line) => (
                <tr key={`${line.serialNumber}-${line.sku}`}>
                  <td className="border-r border-t border-black px-1 py-1 text-center">{line.serialNumber}</td>
                  <td className="border-r border-t border-black px-1 py-1">{purchase.billNumber || purchase.grnNumber || "—"}</td>
                  <td className="border-r border-t border-black px-1 py-1">{formatDate(purchase.billDate || purchase.receivedAt)}</td>
                  <td className="border-r border-t border-black px-1 py-1"><p className="font-bold">{line.articleNumber || line.product}</p>{line.articleNumber && <p>{line.product}</p>}</td>
                  <td className="border-r border-t border-black px-1 py-1">{line.size || line.variant || "—"}</td>
                  <td className="border-r border-t border-black px-1 py-1 text-center">{line.quantity}</td>
                  <td className="border-r border-t border-black px-1 py-1">{line.hsnCode || "—"}</td>
                  <td className="border-r border-t border-black px-1 py-1 text-right">{Number(line.rate || 0).toFixed(2)}</td>
                  <td className="border-t border-black px-1 py-1 text-right">{Number(line.grossAmount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="grid min-h-[140px] grid-cols-[1.55fr_.95fr] border-x border-b border-black">
          <div />
          <div className="border-l border-black text-base font-bold">
            <PaperTotal label="Gross Amount" value={totals.grossAmount} />
            <PaperTotal label="Less Disc." value={totals.discountAmount} />
            <PaperTotal label="Taxable Amt" value={totals.taxableAmount} />
            <PaperTotal label="Total CGST Amt" value={totals.cgstAmount} />
            <PaperTotal label="Total SGST Amt" value={totals.sgstAmount} />
            <PaperTotal label="Total IGST Amt" value={totals.igstAmount} />
            <PaperTotal label="Round Off." value={totals.roundOff} />
            <PaperTotal label="Net Amount" value={totals.totalAmount} emphasis />
          </div>
        </section>

        <section className="mt-4 w-[78%] border border-black font-sans text-[10px]">
          <table className="w-full border-collapse">
            <thead className="bg-[#ede8d5] text-center font-bold"><tr>{["HSN CODE", "TAX %", "QTY", "Amount", "CGST", "SGST/IGST", "TOTAL AMT."].map((heading) => <th key={heading} className="border-r border-black px-2 py-2 last:border-r-0">{heading}</th>)}</tr></thead>
            <tbody>
              {debitNote.taxSummary.map((row) => <tr key={`${row.hsnCode}-${row.taxRate}`}><td className="border-r border-t border-black px-2 py-1">{row.hsnCode}</td><td className="border-r border-t border-black px-2 py-1">{row.taxRate}%</td><td className="border-r border-t border-black px-2 py-1 text-right">{row.quantity}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(row.taxableAmount).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(row.cgstAmount).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number((row.sgstAmount || 0) + (row.igstAmount || 0)).toFixed(2)}</td><td className="border-t border-black px-2 py-1 text-right">{Number(row.totalAmount).toFixed(2)}</td></tr>)}
              <tr className="font-bold"><td className="border-r border-t border-black px-2 py-1">GRAND TOTALS</td><td className="border-r border-t border-black" /><td className="border-r border-t border-black px-2 py-1 text-right">{totals.quantity}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(totals.taxableAmount || 0).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(totals.cgstAmount || 0).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number((totals.sgstAmount || 0) + (totals.igstAmount || 0)).toFixed(2)}</td><td className="border-t border-black px-2 py-1 text-right">{Number(totals.totalAmount || 0).toFixed(2)}</td></tr>
            </tbody>
          </table>
        </section>

        <section className="mt-5 w-[82%] font-sans text-[10px]">
          <p className="mb-1 font-serif text-sm underline">GST SUMMARY</p>
          <table className="w-full border border-black border-collapse"><thead className="bg-[#ede8d5]"><tr>{["GST%", "TAXABLE AMOUNT", "CGST AMT", "SGST AMT", "IGST AMT", "TOTAL TAX"].map((heading) => <th key={heading} className="border-r border-black px-2 py-2 last:border-r-0">{heading}</th>)}</tr></thead><tbody>{debitNote.taxSummary.map((row) => <tr key={`gst-${row.hsnCode}-${row.taxRate}`}><td className="border-r border-t border-black px-2 py-1">{row.taxRate}%</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(row.taxableAmount).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(row.cgstAmount).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(row.sgstAmount).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(row.igstAmount).toFixed(2)}</td><td className="border-t border-black px-2 py-1 text-right">{Number((row.cgstAmount || 0) + (row.sgstAmount || 0) + (row.igstAmount || 0)).toFixed(2)}</td></tr>)}<tr className="font-bold"><td className="border-r border-t border-black px-2 py-1">GRAND TOTALS</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(totals.taxableAmount || 0).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(totals.cgstAmount || 0).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(totals.sgstAmount || 0).toFixed(2)}</td><td className="border-r border-t border-black px-2 py-1 text-right">{Number(totals.igstAmount || 0).toFixed(2)}</td><td className="border-t border-black px-2 py-1 text-right">{Number(totals.taxAmount || 0).toFixed(2)}</td></tr></tbody></table>
        </section>

        <footer className="mt-10 grid grid-cols-2 items-end text-sm font-bold"><div><p>E-INVOICE SCAN CODE BELOW</p><div className="mt-3 flex h-24 w-24 items-center justify-center border border-dashed border-black p-2 text-center text-[8px] font-normal">QR/IRN not available</div></div><div className="text-right"><p>For {companyName}</p><p className="mt-12">Authorised Signatory</p></div></footer>
      </article>
    </div>
  );
}

function PaperField({ label, value }) {
  return <><p className="border-b border-r border-black px-2 py-1 font-bold">{label} <span className="float-right">:-</span></p><p className="border-b border-black px-2 py-1 font-bold">{value}</p></>;
}

function PaperTotal({ label, value, emphasis = false }) {
  return <div className={`grid grid-cols-[1fr_1fr] border-b border-black px-3 py-1 last:border-b-0 ${emphasis ? "text-lg" : ""}`}><p>{label}</p><p className="text-right">{Number(value || 0).toFixed(2)}</p></div>;
}
